import { FC } from 'react';

import { useFormik } from 'formik';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { groupPromoCodesFormInitialValues } from '../form';
import { PromoCodeRewardPicker } from '../promoCodeRewardPicker/PromoCodeRewardPicker';
import { buildPromoCodePayload, getPromoCodeCountByType } from '../utils';

import { promoCodeService } from 'v2/api/promoCodeService';
import { MarkedAutocomplete } from 'v2/components/autocomplete/markedAutocomplete/MarkedAutocomplete';
import { PromoCodeType } from 'v2/constants';
import { GroupPromoCodeForm } from 'v2/types/PromoCodeModel';
import { handleHttpError } from 'v2/utils';

interface AddPromoCodesGroupDialogProps {
  isOpen: boolean;
  onClose: VoidFunction;
  onRevalidate: VoidFunction;
}

export const AddPromoCodesGroupDialog: FC<AddPromoCodesGroupDialogProps> = ({ isOpen, onClose, onRevalidate }) => {
  const { trigger, isMutating } = useSWRMutation('createPromoCodesGroup', promoCodeService.createPromoCodesGroup, {
    onSuccess: () => {
      onRevalidate();
      toast.success('Promo codes group has been created successfully');
      handleClose();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<GroupPromoCodeForm>({
    initialValues: groupPromoCodesFormInitialValues,
    onSubmit: (values) => {
      trigger(buildPromoCodePayload(values));
    },
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const handleRewardTypeChange = (value: PromoCodeType) => {
    formik.setFieldValue('type', value);
    formik.setFieldValue('value', '');
    formik.setFieldValue('count', getPromoCodeCountByType(value));
  };

  const handleRewardValueChange = (value: string) => {
    formik.setFieldValue('value', value);
  };

  const handleRewardCountChange = (value?: number) => {
    formik.setFieldValue('count', value);
  };

  const handlePromoCodesNamesChange = (value: string[]) => {
    formik.setFieldValue('promocodesNames', value);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Promo Codes Group</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="groupName"
            value={formik.values.groupName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Group Name"
            required
          />

          <MarkedAutocomplete
            label="Promo Code's Names"
            value={formik.values.promocodesNames}
            options={formik.values.promocodesNames}
            onChange={handlePromoCodesNamesChange}
            required
          />

          <PromoCodeRewardPicker
            type={formik.values.type}
            onTypeChange={handleRewardTypeChange}
            value={formik.values.value}
            onValueChange={handleRewardValueChange}
            count={formik.values.count}
            onCountChange={handleRewardCountChange}
          />

          <TextField
            name="activationNumber"
            value={formik.values.activationNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Number Of Activations"
            type="number"
            required
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button loading={isMutating} type="submit" variant="contained">
            Add
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
